// File: apps/web/src/features/import/ProgressStrip.tsx
// Version: 2026.4.7 | Sprint 7 Wave 3 — Live import progress strip
//
// Pure presentational component — takes an ImportProgress and renders the
// current state. The hook (useImportProgress) lives in ImportPage.

import { ENGINE_COLORS as C, ENGINE_FONTS as F } from '../../components/engine/tokens';
import { GlassPanel, PulseDot } from '../../components/engine/EnginePageShell';
import type { ImportProgress } from '../../lib/signalr/importHub';

interface ProgressStripProps {
  progress: ImportProgress;
  batchId:  string;
}

export function ProgressStrip({ progress, batchId }: ProgressStripProps) {
  const palette = STATE_PALETTE[progress.state];
  const pct = progress.totalRows > 0
    ? Math.min(100, (progress.completed / progress.totalRows) * 100)
    : 0;

  return (
    <GlassPanel accent={palette.color}>
      <div style={{ padding: 24 }}>
        {/* Header row: state + batchId */}
        <div style={{
          display:        'flex',
          justifyContent: 'space-between',
          alignItems:     'center',
          marginBottom:   16,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {progress.state === 'in-progress' || progress.state === 'subscribed' || progress.state === 'connecting' ? (
              <PulseDot color={palette.color} size={10} />
            ) : (
              <span style={{ fontSize: 16, color: palette.color }}>{palette.icon}</span>
            )}
            <span style={{
              fontSize:      13,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color:         palette.color,
              fontWeight:    600,
              fontFamily:    F.display,
            }}>
              {palette.label}
            </span>
          </div>
          <code style={{
            fontSize:   11,
            color:      C.dim,
            fontFamily: F.mono,
          }}>
            {batchId}
          </code>
        </div>

        {/* Progress bar */}
        {progress.totalRows > 0 && (
          <>
            <div style={{
              position:     'relative',
              height:       8,
              background:   C.surface,
              border:       `1px solid ${C.faint}`,
              borderRadius: 4,
              overflow:     'hidden',
              marginBottom: 12,
            }}>
              <div style={{
                position:   'absolute',
                inset:      '0 auto 0 0',
                width:      `${pct}%`,
                background: `linear-gradient(90deg, ${palette.color}, ${palette.color}dd)`,
                boxShadow:  `0 0 16px ${palette.color}88`,
                transition: 'width 0.3s ease-out',
              }} />
            </div>

            <div style={{
              display:        'flex',
              justifyContent: 'space-between',
              fontSize:       12,
              color:          C.muted,
              fontFamily:     F.mono,
            }}>
              <span>
                <span style={{ color: C.text, fontWeight: 600 }}>{progress.completed}</span>
                {' / '}
                <span style={{ color: C.text }}>{progress.totalRows}</span>
                {' classified'}
              </span>
              <span>
                {progress.succeeded > 0 && (
                  <span style={{ color: C.neon }}>{progress.succeeded} ✓</span>
                )}
                {progress.failed > 0 && (
                  <>
                    {progress.succeeded > 0 && <span style={{ color: C.dim }}> · </span>}
                    <span style={{ color: C.coral }}>{progress.failed} ✕</span>
                  </>
                )}
                {' · '}
                {pct.toFixed(0)}%
              </span>
            </div>
          </>
        )}

        {/* Empty / connecting state body */}
        {progress.totalRows === 0 && progress.state !== 'failed' && (
          <div style={{
            fontSize:   13,
            color:      C.muted,
            fontFamily: F.display,
            padding:    '8px 0',
          }}>
            {progress.state === 'connecting'
              ? 'Connecting to import hub…'
              : 'Waiting for first message…'}
          </div>
        )}

        {/* Completion summary */}
        {progress.state === 'completed' && progress.summary && (
          <div style={{
            marginTop:    16,
            padding:      '12px 16px',
            background:   `${C.neon}08`,
            border:       `1px solid ${C.neon}28`,
            borderRadius: 8,
            fontSize:     13,
            color:        C.muted,
            fontFamily:   F.display,
          }}>
            Classification complete ·{' '}
            <span style={{ color: C.neon, fontWeight: 600 }}>
              ${progress.summary.totalCostUSD.toFixed(4)}
            </span>{' '}
            spent · check the Staged tab to review and commit
          </div>
        )}

        {/* Failure state */}
        {progress.state === 'failed' && (
          <div style={{
            marginTop:    16,
            padding:      '12px 16px',
            background:   `${C.coral}08`,
            border:       `1px solid ${C.coral}38`,
            borderRadius: 8,
            fontSize:     13,
            color:        C.coral,
            fontFamily:   F.display,
          }}>
            <strong>Connection or job failure.</strong>{' '}
            {progress.error ?? 'Unknown error.'} The staged rows are still safe — refresh the Staged tab to review them.
          </div>
        )}
      </div>
    </GlassPanel>
  );
}

// ─── State palette ─────────────────────────────────────────────────────────

const STATE_PALETTE: Record<ImportProgress['state'], { color: string; label: string; icon: string }> = {
  idle:          { color: C.dim,    label: 'IDLE',         icon: '·' },
  connecting:    { color: C.cyan,   label: 'CONNECTING',   icon: '◌' },
  subscribed:    { color: C.cyan,   label: 'SUBSCRIBED',   icon: '◉' },
  'in-progress': { color: C.violet, label: 'CLASSIFYING',  icon: '▶' },
  completed:     { color: C.neon,   label: 'COMPLETE',     icon: '✓' },
  failed:        { color: C.coral,  label: 'FAILED',       icon: '✕' },
};
